import React, {
  useCallback,
  useMemo,
  useRef,
  useState,
  useEffect,
} from 'react';
import { useDispatch } from 'react-redux';

import SortIcon from 'mdi-react/SwapVerticalIcon';

import { useAppContext } from '@prisjakt/ui-app-context';
import { useViewportContext } from 'src/components/viewport/context';

import { ParamsAndReturnTypes } from '@prisjakt/ui-translate/src/translate-func';

import { useActiveCampaignByProductId } from 'src/hooks/active-campaign-by-product-id';

import { CompactListLayout } from 'src/areas/product-collection/components/products/layout-compact-list';
import { ListLayout } from 'src/areas/product-collection/components/products/layout-list';
import { GridLayout } from 'src/areas/product-collection/components/products/layout-grid';
import Pagination from 'src/areas/product-collection/components/productOffersContainer/pagination';
import { ResultFilters } from '../../filters';
import { SearchResultsProductsHeader } from '../../panel-header';
import { SortedBy } from '../../sorted-by';
import { productPriceClick } from 'src/areas/product/actions/product';
import ToggleButton from 'src/areas/product-collection/components/toggle-button';
import OverlaySort from 'src/areas/product-collection/components/productOffersContainer/overlay-sort';
import { getTranslationForSortOption } from 'src/areas/product-collection/components/productOffersContainer/sort-options';
import { Option } from 'src/components/filter-dropdown';
import { SearchResultsFeedback } from '../../feedback';
import { NoSearchResults } from 'src/components/no-search-results';

import { useLocalStorage } from 'src/hooks/use-localstorage';
import { pushEvent } from 'src/client/data-layer';

import { MAX_RESULTS } from '../../../constants';
import { NAV_HEIGHT } from 'src/components/sticky-tab-nav/constants';

import {
  trackSearchResultProductClicked,
  trackSearchResultProductsListed,
  trackSearchResultSwitchLayout,
} from '../../../tracking';

import {
  Product,
  Raw,
  Offer,
  SearchFacetInterval,
  SearchFacetList,
  SearchPageInfo,
  SortInfo,
  SearchResultFilters,
} from 'src/types';
import { LayoutMode } from 'src/components/layout-buttons/types';
import { TabAnchor } from '../../navigation/types';
import { Filter, SearchFacetType } from '../types';

import * as S from '../styles';
import { MobileSortButtonWrapper } from './styles';
import { useShowFeedback } from 'src/hooks/use-show-feedback';
import {
  useTrailingSkyscraperExperiment,
  useSearchUnfeaturedClickout,
} from 'src/hooks/experiments';
import { useEffectOnMount } from 'src/hooks/useEffectOnMount';

type NewFacets = SearchResultFilters;

type OldFacets = (SearchFacetList | SearchFacetInterval | null)[] | null;

type Props = {
  active: boolean;
  facets?: OldFacets | NewFacets;
  pageInfo?: SearchPageInfo | null;
  products: (
    | Omit<Product, 'classicFullPath' | 'conditions' | 'media'>
    | Omit<Raw, 'store'>
    | Omit<
        Offer,
        | 'stockStatus'
        | 'id'
        | 'store'
        | 'media'
        | 'name'
        | 'externalUri'
        | 'offerPrice'
      >
    | null
  )[];
  productsCount: number;
  sort?: SortInfo | null;
  query: string;
  newSearch: boolean;
  isWide?: boolean;
};

const ADS_LAYOUT = {
  [LayoutMode.GRID]: {
    panorama_extra: { show: false, row: 2 },
    dubbelpanorama1: { show: true, row: 2 },
    dubbelpanorama2: { show: true, row: 8 },
  },
  [LayoutMode.LIST]: {
    panorama_extra: { show: false, row: 2 },
    dubbelpanorama1: { show: true, row: 3 },
    dubbelpanorama2: { show: true, row: 8 },
  },
  [LayoutMode.COMPACT_LIST]: {
    panorama_extra: { show: false, row: 2 },
    dubbelpanorama1: { show: true, row: 3 },
    dubbelpanorama2: { show: true, row: 15 },
  },
};

const ONE_DAY = 86400 * 1000;

export const ProductResults = (props: Props) => {
  const {
    active = true,
    facets,
    pageInfo,
    products,
    productsCount,
    sort,
    query,
    newSearch,
    isWide,
  } = props;

  const { t } = useAppContext();
  const dispatch = useDispatch();
  const { isDesktop } = useViewportContext();
  const productsContainer = useRef<HTMLDivElement>(null);

  const [activeLayout, setLayoutState] = useState<LayoutMode>(
    LayoutMode.COMPACT_LIST
  );
  const [mobileSortOpen, setMobileSortOpen] = useState(false);

  const [searchUXFeedbackTime] = useLocalStorage('pj:searchUXFeedbackTime', 0);
  const [searchRelevancyFeedbackTime] = useLocalStorage(
    'pj:searchRelevancyFeedbackTime',
    0
  );

  const showUXFeedback = useShowFeedback(
    0.5,
    ONE_DAY * 7,
    searchUXFeedbackTime
  );

  const showRelevancyFeedback = useShowFeedback(
    0.1,
    ONE_DAY * 2,
    searchRelevancyFeedbackTime
  );

  const [persistentLayout, setPersistentLayout] = useLocalStorage(
    'pj:searchResultsLayout',
    LayoutMode.COMPACT_LIST
  );
  const trailingSkyscraperVariant = useTrailingSkyscraperExperiment();
  const unfeaturedClickout = useSearchUnfeaturedClickout();

  useEffectOnMount(() => {
    trackSearchResultProductsListed(activeLayout);
  });

  useEffect(() => {
    if (activeLayout) {
      dispatch({
        type: 'ADS_VIEW_UPDATE',
        view: `search:layout:${activeLayout}`,
      });
    }
  }, [activeLayout, dispatch]);

  useEffectOnMount(() => {
    // Change layout to the last used mode saved in localStorage
    if (persistentLayout) {
      setLayoutState(persistentLayout);
    }
  });

  useEffect(() => {
    if (trailingSkyscraperVariant === 'trailing and refreshing skyscraper') {
      dispatch({
        type: 'ADS_PATHKEY_UPDATE',
        pathKey: `search:offset:${pageInfo?.offset}`,
      });
    }
  }, [dispatch, pageInfo?.offset, trailingSkyscraperVariant]);

  const mobileSortIcon = useMemo(() => <SortIcon size={18} />, []);

  const availableSortTranslations: Record<string, string> = {
    'score:desc': 'relevance',
    'lowest_price:asc': 'price:asc',
    'lowest_price:desc': 'price:desc',
    'product_name:asc': 'name',
    'user_rating:desc': 'user_reviews',
  };

  const currentSort = `${sort?.name}:${sort?.direction}`;

  const currentTranslatedSort = getTranslationForSortOption(
    currentSort,
    availableSortTranslations,
    t
  );

  const productIds = useMemo(
    () =>
      products?.length
        ? (products
            .map(product => (product && 'id' in product ? product.id : null))
            .filter(id => id) as number[])
        : [],
    [products]
  );
  const productsWithActiveCampaign = useActiveCampaignByProductId(
    productIds,
    true
  );

  const setLayout = (layout: LayoutMode) => {
    if (activeLayout !== layout) {
      trackSearchResultSwitchLayout(activeLayout, layout);
    }

    setLayoutState(layout);
    setPersistentLayout(layout);
  };

  const handlePaginationClick = () => {
    if (!IS_BROWSER || !productsContainer.current?.offsetTop) {
      return;
    }

    window.scrollTo({
      top: productsContainer.current?.offsetTop - NAV_HEIGHT,
      behavior: 'smooth',
    });
  };

  const getFacetFilters = (): Filter[] | undefined => {
    const oldFacets = facets as OldFacets;
    const filters = oldFacets?.map(facet => {
      if (!facet) return null;

      const filterTitle = t(
        'search',
        `facets.title-${facet.id}` as keyof ParamsAndReturnTypes['search']
      );

      switch (facet.__typename) {
        case SearchFacetType.SEARCH_FACET_LIST:
          return {
            options: facet.buckets
              .map(item => {
                if (!item) return null;
                return {
                  ...item,
                  value: item?.name,
                };
              })
              .flatMap(item => (item ? [item] : [])),
            title: filterTitle,
            type: SearchFacetType.SEARCH_FACET_LIST,
            id: facet.id,
          };
        default:
          return null;
      }
    });

    return filters?.flatMap(item => (item ? [item] : []));
  };

  const onProductClickHandler = (
    id: string | number,
    name?: string,
    type?: string
  ) => {
    if (type === 'Raw' || type === 'Offer') {
      const clickedProduct = products.find(p => {
        if (p?.__typename === 'Raw' && (p as Raw)?.name === name) {
          return true;
        }

        if (p?.__typename === 'Offer' && (p as Offer)?.id === id) {
          return true;
        }

        return false;
      }) as Offer | Raw | undefined;

      dispatch(
        productPriceClick(
          null,
          null,
          Boolean(clickedProduct?.store?.featured),
          'raw'
        )
      );

      // Track revenue if store is featured
      if (clickedProduct?.store?.featured) {
        pushEvent('rawPriceClickRevenue', { name });
      }
    }

    if (type === 'Product') trackSearchResultProductClicked(id, name);
  };

  const toggleMobileSort = useCallback(() => {
    setMobileSortOpen(!mobileSortOpen);
  }, [mobileSortOpen]);

  const onClose = useCallback(() => {
    setMobileSortOpen(false);
  }, []);

  const newFacets = facets as NewFacets;
  const newFilters = [
    {
      id: 'category_id',
      type: 'SearchFacetList',
      title: t('search', 'facets.title-category_id'),
      options: newFacets?.category?.map(
        f =>
          f && {
            name: f.name,
            value: f.name,
            id: f.id,
            count: f.productCount,
          }
      ),
    },
    {
      id: 'brand_id',
      type: 'SearchFacetList',
      title: t('search', 'facets.title-brand_id'),
      options: newFacets?.brand?.map(
        f =>
          f && {
            name: f.name,
            value: f.name,
            id: f.id,
            count: f.productCount,
          }
      ),
    },
  ]
    .filter(Boolean)
    .filter(item => item.options && item.options.length > 0) as Filter[];

  const filters = newSearch ? newFilters : getFacetFilters();
  const totalProducts = pageInfo?.total || 0;

  const sortOptions: Option[] = [
    {
      title: t('common', 'sort.relevance'),
      value: `score:desc`,
    },
    {
      title: t('common', `sort.price:asc`),
      value: `lowest_price:asc`,
    },
    {
      title: t('common', `sort.price:desc`),
      value: `lowest_price:desc`,
    },
    {
      title: t('common', `sort.name`),
      value: `product_name:asc`,
    },
    {
      title: t('common', `sort.user_reviews`),
      value: `user_rating:desc`,
    },
  ];

  return (
    <div ref={productsContainer}>
      <S.Section
        id={TabAnchor.PRODUCTS}
        $active={active}
        $tab={TabAnchor.PRODUCTS}
      >
        <SearchResultsProductsHeader
          activeLayout={activeLayout}
          productsCount={productsCount}
          setLayout={setLayout}
          currentSort={currentSort}
          sortOptions={sortOptions}
          newSearch={newSearch}
        />

        <S.FilterWrapper>
          {filters ? (
            <ResultFilters
              productsCount={productsCount}
              filters={filters}
              query={query}
            />
          ) : null}

          {!isDesktop && !newSearch ? (
            <>
              {mobileSortOpen && (
                <OverlaySort
                  currentSort={currentSort}
                  onClose={onClose}
                  sortOptions={
                    sortOptions.map(option => option.value) as string[]
                  }
                  sortTranslations={availableSortTranslations}
                />
              )}

              <MobileSortButtonWrapper>
                <ToggleButton
                  variant="outlined"
                  icon={mobileSortIcon}
                  onToggle={toggleMobileSort}
                  title={currentTranslatedSort}
                  truncate
                  data-test="SortOrderButton"
                />
              </MobileSortButtonWrapper>
            </>
          ) : (
            !isDesktop && newSearch && <SortedBy />
          )}
        </S.FilterWrapper>

        {!products.length ? (
          <NoSearchResults />
        ) : (
          <>
            {activeLayout === LayoutMode.GRID && (
              <GridLayout
                feedbackPrompt={
                  <SearchResultsFeedback
                    query={query}
                    showRelevancyFeedback={showRelevancyFeedback}
                    showUXFeedback={showUXFeedback}
                  />
                }
                feedbackPromptPosition={3}
                productsWithActiveCampaign={productsWithActiveCampaign}
                products={
                  products.filter(
                    p =>
                      p?.__typename === 'Product' ||
                      p?.__typename === 'Raw' ||
                      p?.__typename === 'Offer'
                  ) as (Product | Raw)[]
                }
                adslayoutConfig={ADS_LAYOUT}
                onClick={onProductClickHandler}
                isWide={isWide}
                unfeaturedClickout={unfeaturedClickout}
              />
            )}
            {activeLayout === LayoutMode.LIST && (
              <ListLayout
                feedbackPrompt={
                  <SearchResultsFeedback
                    query={query}
                    showRelevancyFeedback={showRelevancyFeedback}
                    showUXFeedback={showUXFeedback}
                  />
                }
                feedbackPromptPosition={3}
                productsWithActiveCampaign={productsWithActiveCampaign}
                products={
                  products.filter(
                    p =>
                      p?.__typename === 'Product' ||
                      p?.__typename === 'Raw' ||
                      p?.__typename === 'Offer'
                  ) as (Product | Raw)[]
                }
                adslayoutConfig={ADS_LAYOUT}
                onClick={onProductClickHandler}
                unfeaturedClickout={unfeaturedClickout}
              />
            )}

            {activeLayout === LayoutMode.COMPACT_LIST && (
              <CompactListLayout
                feedbackPrompt={
                  <SearchResultsFeedback
                    query={query}
                    showRelevancyFeedback={showRelevancyFeedback}
                    showUXFeedback={showUXFeedback}
                  />
                }
                feedbackPromptPosition={3}
                productsWithActiveCampaign={productsWithActiveCampaign}
                products={
                  products.filter(
                    p =>
                      p?.__typename === 'Product' ||
                      p?.__typename === 'Raw' ||
                      p?.__typename === 'Offer'
                  ) as (Product | Raw)[]
                }
                adslayoutConfig={ADS_LAYOUT}
                onClick={onProductClickHandler}
                unfeaturedClickout={unfeaturedClickout}
              />
            )}

            {pageInfo?.limit && productsCount > pageInfo?.limit && (
              <Pagination
                pageInfo={pageInfo}
                total={
                  totalProducts >= MAX_RESULTS ? MAX_RESULTS : totalProducts
                }
                onClick={handlePaginationClick}
              />
            )}
          </>
        )}
      </S.Section>
    </div>
  );
};
